import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircleIcon, ArrowRightIcon, HomeIcon, TrendingUpIcon, DollarSignIcon, ClockIcon, ShieldCheckIcon, DownloadIcon, ChevronRightIcon } from 'lucide-react';
// --- Types ---
type StateOption = 'NSW' | 'VIC' | 'QLD' | 'WA' | 'SA' | 'TAS' | 'ACT' | 'NT';
type PurposeOption = 'Living in it' | 'Investment';
type GoalOption = 'Can I buy now?' | 'Should I wait?' | 'Just exploring';
interface FormData {
  savings: number;
  purchasePrice: number;
  annualIncome: number;
  partnerIncome: number;
  secondaryIncome: number;
  weeklyRent: number;
  state: StateOption;
  purpose: PurposeOption;
  goal: GoalOption;
  name: string;
  email: string;
  phone: string;
}
// --- Helpers ---
const formatCurrency = (val: number) => new Intl.NumberFormat('en-AU', {
  style: 'currency',
  currency: 'AUD',
  maximumFractionDigits: 0
}).format(val);
export function FHBClarityTool() {
  // --- State ---
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    savings: 50000,
    purchasePrice: 550000,
    annualIncome: 85000,
    partnerIncome: 0,
    secondaryIncome: 0,
    weeklyRent: 500,
    state: 'WA',
    purpose: 'Living in it',
    goal: 'Just exploring',
    name: '',
    email: '',
    phone: ''
  });
  // Step 2 specific state
  const [waitMonths, setWaitMonths] = useState<number>(0);
  // --- Handlers ---
  const handleInputChange = (field: keyof FormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const nextStep = () => setCurrentStep(prev => Math.min(prev + 1, 8));
  const prevStep = () => setCurrentStep(prev => Math.max(prev - 1, 1));
  // --- Calculations ---
  // Step 2: Cost of Waiting
  const waitCalculations = useMemo(() => {
    const annualGrowthRate = 0.05; // 5%
    const totalIncome = formData.annualIncome + formData.partnerIncome + formData.secondaryIncome;
    const monthlySurplus = totalIncome / 12 - formData.weeklyRent * 4.33;
    const monthlySavings = Math.max(0, monthlySurplus * 0.3); // 30% of surplus to savings
    const futurePrice = formData.purchasePrice * (1 + annualGrowthRate * (waitMonths / 12));
    const targetDeposit = futurePrice * 0.05; // Assuming 5% deposit needed
    const projectedSavings = formData.savings + monthlySavings * waitMonths;
    const extraDeposit = targetDeposit - projectedSavings;
    const rentPaid = formData.weeklyRent * 4.33 * waitMonths;
    const missedGrowth = futurePrice - formData.purchasePrice;
    return {
      futurePrice,
      extraDeposit: Math.max(0, extraDeposit),
      rentPaid,
      missedGrowth,
      totalCost: rentPaid + missedGrowth
    };
  }, [formData, waitMonths]);
  // Step 3: Pathway
  const pathwayCalculations = useMemo(() => {
    const depositPercent = formData.savings / formData.purchasePrice * 100;
    const totalIncome = formData.annualIncome + formData.partnerIncome + formData.secondaryIncome;
    const borrowingEstimate = totalIncome * 6; // Simplified 6x DTI
    const canAfford = borrowingEstimate + formData.savings;
    const isReady = canAfford >= formData.purchasePrice && depositPercent >= 5;
    return {
      depositPercent,
      borrowingEstimate,
      canAfford,
      isReady
    };
  }, [formData]);
  // --- Sub-components ---
  const ProgressBar = () => {
    const phases = [{
      label: 'Your Position',
      steps: [1]
    }, {
      label: 'Cost of Waiting',
      steps: [2]
    }, {
      label: 'Your Pathway',
      steps: [3, 4]
    }, {
      label: 'Your Result',
      steps: [5, 6, 7, 8]
    }];
    const currentPhaseIndex = phases.findIndex(p => p.steps.includes(currentStep));
    return <div className="mb-8" data-id="element-488">
        <div className="flex justify-between mb-2" data-id="element-489">
          {phases.map((phase, idx) => {
          const isActive = currentPhaseIndex === idx;
          const isPast = currentPhaseIndex > idx;
          return <div key={idx} className="flex flex-col items-center w-1/4" data-id="element-490">
                <div className="text-xs font-medium mb-2 text-center" data-id="element-491">
                  <span className={isActive ? 'text-primary-600 dark:text-primary-400' : isPast ? 'text-slate-600 dark:text-slate-300' : 'text-slate-400 dark:text-slate-500'} data-id="element-492">
                    {phase.label}
                  </span>
                </div>
                <div className="w-full h-2 bg-slate-100 dark:bg-white/5 rounded-full overflow-hidden relative" data-id="element-493">
                  {(isActive || isPast) && <motion.div initial={{
                width: 0
              }} animate={{
                width: isPast ? '100%' : '50%'
              }} className={`absolute top-0 left-0 h-full ${isPast ? 'bg-accent-500' : 'bg-gradient-to-r from-accent-500 to-primary-500'}`} data-id="element-494" />}
                </div>
              </div>;
        })}
        </div>
      </div>;
  };
  // --- Step Renders ---
  const renderStep1 = () => <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: -20
  }} className="space-y-8" data-id="element-495">
      <div className="text-center" data-id="element-496">
        <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-3" data-id="element-497">
          Tell us where you're at
        </h2>
        <p className="text-slate-500 dark:text-slate-400" data-id="element-498">
          Answer a few quick questions so we can map out your position and
          possible next step.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-499">
        {/* Financials */}
        <div className="space-y-4" data-id="element-500">
          <div data-id="element-501">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1" data-id="element-502">
              How much do you have saved?
            </label>
            <div className="relative" data-id="element-503">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" data-id="element-504">
                $
              </span>
              <input type="number" value={formData.savings} onChange={e => handleInputChange('savings', Number(e.target.value))} className="w-full pl-8 pr-4 py-3 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-[#0f1629] text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 outline-none transition-all" data-id="element-505" />
            </div>
          </div>
          <div data-id="element-506">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1" data-id="element-507">
              What price range are you looking to buy in?
            </label>
            <p className="text-xs text-slate-500 mb-2" data-id="element-508">
              Your ideal purchase price or target budget
            </p>
            <div className="relative" data-id="element-509">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" data-id="element-510">
                $
              </span>
              <input type="number" value={formData.purchasePrice} onChange={e => handleInputChange('purchasePrice', Number(e.target.value))} className="w-full pl-8 pr-4 py-3 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-[#0f1629] text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 outline-none transition-all" data-id="element-511" />
            </div>
          </div>
          <div data-id="element-512">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1" data-id="element-513">
              What is your annual income?
            </label>
            <div className="relative" data-id="element-514">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" data-id="element-515">
                $
              </span>
              <input type="number" value={formData.annualIncome} onChange={e => handleInputChange('annualIncome', Number(e.target.value))} className="w-full pl-8 pr-4 py-3 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-[#0f1629] text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 outline-none transition-all" data-id="element-516" />
            </div>
          </div>
          <div data-id="element-517">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1" data-id="element-518">
              Partner income (optional)
            </label>
            <p className="text-xs text-slate-500 mb-2" data-id="element-519">
              Only include if buying together
            </p>
            <div className="relative" data-id="element-520">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" data-id="element-521">
                $
              </span>
              <input type="number" value={formData.partnerIncome} onChange={e => handleInputChange('partnerIncome', Number(e.target.value))} className="w-full pl-8 pr-4 py-3 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-[#0f1629] text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 outline-none transition-all" data-id="element-522" />
            </div>
          </div>
          <div data-id="element-523">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1" data-id="element-524">
              Secondary income (optional)
            </label>
            <p className="text-xs text-slate-500 mb-2" data-id="element-525">
              e.g. overtime, bonuses, side income
            </p>
            <div className="relative" data-id="element-526">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" data-id="element-527">
                $
              </span>
              <input type="number" value={formData.secondaryIncome} onChange={e => handleInputChange('secondaryIncome', Number(e.target.value))} className="w-full pl-8 pr-4 py-3 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-[#0f1629] text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 outline-none transition-all" data-id="element-528" />
            </div>
          </div>
        </div>

        {/* Other Details */}
        <div className="space-y-4" data-id="element-529">
          <div data-id="element-530">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1" data-id="element-531">
              How much are you paying in rent each week?
            </label>
            <div className="relative" data-id="element-532">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" data-id="element-533">
                $
              </span>
              <input type="number" value={formData.weeklyRent} onChange={e => handleInputChange('weeklyRent', Number(e.target.value))} className="w-full pl-8 pr-12 py-3 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-[#0f1629] text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 outline-none transition-all" data-id="element-534" />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500" data-id="element-535">
                /wk
              </span>
            </div>
          </div>
          <div data-id="element-536">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1" data-id="element-537">
              What state are you buying in?
            </label>
            <select value={formData.state} onChange={e => handleInputChange('state', e.target.value)} className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-[#0f1629] text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 outline-none transition-all appearance-none" data-id="element-538">
              {['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'].map(s => <option key={s} value={s} data-id="element-539">
                    {s}
                  </option>)}
            </select>
          </div>
          <div data-id="element-540">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2" data-id="element-541">
              Is this for:
            </label>
            <div className="flex p-1 bg-slate-100 dark:bg-white/5 rounded-xl" data-id="element-542">
              {['Living in it', 'Investment'].map(opt => <button key={opt} onClick={() => handleInputChange('purpose', opt)} className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all ${formData.purpose === opt ? 'bg-white dark:bg-[#1a2035] text-slate-900 dark:text-white shadow-sm' : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'}`} data-id="element-543">
                  {opt}
                </button>)}
            </div>
          </div>
          <div data-id="element-544">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2" data-id="element-545">
              What do you want to know?
            </label>
            <div className="flex flex-col sm:flex-row gap-2 p-1 bg-slate-100 dark:bg-white/5 rounded-xl" data-id="element-546">
              {['Can I buy now?', 'Should I wait?', 'Just exploring'].map(opt => <button key={opt} onClick={() => handleInputChange('goal', opt)} className={`flex-1 py-2 px-2 text-xs sm:text-sm font-medium rounded-lg transition-all ${formData.goal === opt ? 'bg-white dark:bg-[#1a2035] text-slate-900 dark:text-white shadow-sm' : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'}`} data-id="element-547">
                    {opt}
                  </button>)}
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end pt-6 border-t border-slate-200 dark:border-white/10" data-id="element-548">
        <button onClick={nextStep} className="px-8 py-4 rounded-xl bg-gradient-to-r from-accent-500 to-primary-500 text-white font-semibold flex items-center gap-2 hover:shadow-lg hover:scale-[1.02] transition-all" data-id="element-549">
          See Cost of Waiting <ArrowRightIcon className="w-5 h-5" data-id="element-550" />
        </button>
      </div>
    </motion.div>;
  const renderStep2 = () => {
    const marks = [{
      value: 0,
      label: 'Buy now'
    }, {
      value: 6,
      label: '6 months'
    }, {
      value: 12,
      label: '12 months'
    }, {
      value: 18,
      label: '18 months'
    }, {
      value: 24,
      label: '24 months'
    }];
    return <motion.div initial={{
      opacity: 0,
      x: 20
    }} animate={{
      opacity: 1,
      x: 0
    }} exit={{
      opacity: 0,
      x: -20
    }} className="space-y-8" data-id="element-551">
        <div className="text-center" data-id="element-552">
          <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-3" data-id="element-553">
            Cost of Waiting
          </h2>
          <p className="text-slate-500 dark:text-slate-400 max-w-2xl mx-auto" data-id="element-554">
            Waiting isn't always the safer option. Sometimes the target moves
            faster than your savings do.
          </p>
        </div>

        <div className="bg-white dark:bg-[#1a2035] p-6 rounded-2xl border border-slate-200 dark:border-white/10 shadow-sm" data-id="element-555">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-6 text-center" data-id="element-556">
            How long are you thinking of waiting?
          </label>
          <div className="px-4 pb-8" data-id="element-557">
            <input type="range" min="0" max="24" step="6" value={waitMonths} onChange={e => setWaitMonths(Number(e.target.value))} className="w-full h-2 bg-slate-200 dark:bg-white/10 rounded-lg appearance-none cursor-pointer accent-primary-500" data-id="element-558" />
            <div className="flex justify-between mt-4 px-1" data-id="element-559">
              {marks.map(mark => <div key={mark.value} className={`text-xs font-medium cursor-pointer transition-colors ${waitMonths === mark.value ? 'text-primary-600 dark:text-primary-400' : 'text-slate-400'}`} onClick={() => setWaitMonths(mark.value)} data-id="element-560">
                  {mark.label}
                </div>)}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-id="element-561">
          <div className="bg-slate-50 dark:bg-white/5 p-4 rounded-xl border border-slate-200 dark:border-white/10" data-id="element-562">
            <p className="text-xs text-slate-500 mb-1" data-id="element-563">
              Estimated Future Price
            </p>
            <p className="text-xl font-bold text-slate-900 dark:text-white" data-id="element-564">
              {formatCurrency(waitCalculations.futurePrice)}
            </p>
          </div>
          <div className="bg-slate-50 dark:bg-white/5 p-4 rounded-xl border border-slate-200 dark:border-white/10" data-id="element-565">
            <p className="text-xs text-slate-500 mb-1" data-id="element-566">Extra Deposit Needed</p>
            <p className="text-xl font-bold text-amber-500" data-id="element-567">
              {formatCurrency(waitCalculations.extraDeposit)}
            </p>
          </div>
          <div className="bg-slate-50 dark:bg-white/5 p-4 rounded-xl border border-slate-200 dark:border-white/10" data-id="element-568">
            <p className="text-xs text-slate-500 mb-1" data-id="element-569">
              Rent Paid While Waiting
            </p>
            <p className="text-xl font-bold text-slate-900 dark:text-white" data-id="element-570">
              {formatCurrency(waitCalculations.rentPaid)}
            </p>
          </div>
          <div className="bg-slate-50 dark:bg-white/5 p-4 rounded-xl border border-slate-200 dark:border-white/10" data-id="element-571">
            <p className="text-xs text-slate-500 mb-1" data-id="element-572">
              Potential Missed Growth
            </p>
            <p className="text-xl font-bold text-accent-500" data-id="element-573">
              {formatCurrency(waitCalculations.missedGrowth)}
            </p>
          </div>
          <div className="bg-slate-50 dark:bg-white/5 p-4 rounded-xl border border-slate-200 dark:border-white/10" data-id="element-574">
            <p className="text-xs text-slate-500 mb-1" data-id="element-575">Time Delayed</p>
            <p className="text-xl font-bold text-slate-900 dark:text-white" data-id="element-576">
              {waitMonths} months
            </p>
          </div>
        </div>

        <div className="bg-gradient-to-br from-slate-900 to-slate-800 p-6 rounded-2xl text-white shadow-xl relative overflow-hidden" data-id="element-577">
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary-500/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-578"></div>
          <div className="relative z-10" data-id="element-579">
            <h3 className="text-lg font-medium text-slate-300 mb-2" data-id="element-580">
              Cost of Inaction
            </h3>
            <div className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-accent-400 to-primary-400 mb-4" data-id="element-581">
              {formatCurrency(waitCalculations.totalCost)}
            </div>
            <p className="text-sm text-slate-400 max-w-xl mb-4" data-id="element-582">
              If prices rise while you wait, your deposit target may move
              further away even while you keep saving.
            </p>
            <ul className="text-xs text-slate-300 space-y-2" data-id="element-583">
              <li className="flex items-center gap-2" data-id="element-584">
                <div className="w-1.5 h-1.5 rounded-full bg-accent-500" data-id="element-585"></div>{' '}
                Assumes 5% annual property growth
              </li>
              <li className="flex items-center gap-2" data-id="element-586">
                <div className="w-1.5 h-1.5 rounded-full bg-accent-500" data-id="element-587"></div>{' '}
                Assumes 30% of surplus income is saved
              </li>
              <li className="flex items-center gap-2" data-id="element-588">
                <div className="w-1.5 h-1.5 rounded-full bg-accent-500" data-id="element-589"></div>{' '}
                Includes rent paid as sunk cost
              </li>
            </ul>
          </div>
        </div>

        <div className="flex justify-between pt-6 border-t border-slate-200 dark:border-white/10" data-id="element-590">
          <button onClick={prevStep} className="px-6 py-3 rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5 transition-colors" data-id="element-591">
            Back
          </button>
          <button onClick={nextStep} className="px-8 py-4 rounded-xl bg-gradient-to-r from-accent-500 to-primary-500 text-white font-semibold flex items-center gap-2 hover:shadow-lg hover:scale-[1.02] transition-all" data-id="element-592">
            See Your Pathway <ArrowRightIcon className="w-5 h-5" data-id="element-593" />
          </button>
        </div>
      </motion.div>;
  };
  const renderStep3 = () => <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} className="space-y-8" data-id="element-594">
      <div className="text-center" data-id="element-595">
        <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-3" data-id="element-596">
          Your Possible Buying Pathway
        </h2>
        <p className="text-slate-500 dark:text-slate-400" data-id="element-597">
          Based on your numbers, here's what may be worth exploring.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-598">
        <div className="bg-white dark:bg-[#1a2035] p-6 rounded-2xl border border-slate-200 dark:border-white/10 shadow-sm" data-id="element-599">
          <div className="flex items-center gap-3 mb-4" data-id="element-600">
            <div className="p-2 bg-primary-50 dark:bg-primary-500/10 rounded-lg" data-id="element-601">
              <TrendingUpIcon className="w-6 h-6 text-primary-600 dark:text-primary-400" data-id="element-602" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white" data-id="element-603">
              Your Metrics
            </h3>
          </div>
          <div className="space-y-4" data-id="element-604">
            <div className="flex justify-between items-center pb-3 border-b border-slate-100 dark:border-white/5" data-id="element-605">
              <span className="text-sm text-slate-500" data-id="element-606">Deposit %</span>
              <span className="font-medium text-slate-900 dark:text-white" data-id="element-607">
                {pathwayCalculations.depositPercent.toFixed(1)}%
              </span>
            </div>
            <div className="flex justify-between items-center pb-3 border-b border-slate-100 dark:border-white/5" data-id="element-608">
              <span className="text-sm text-slate-500" data-id="element-609">Est. Borrowing</span>
              <span className="font-medium text-slate-900 dark:text-white" data-id="element-610">
                {formatCurrency(pathwayCalculations.borrowingEstimate)}
              </span>
            </div>
            <div className="flex justify-between items-center" data-id="element-611">
              <span className="text-sm text-slate-500" data-id="element-612">Total Capacity</span>
              <span className="font-bold text-primary-600 dark:text-primary-400" data-id="element-613">
                {formatCurrency(pathwayCalculations.canAfford)}
              </span>
            </div>
          </div>
        </div>

        <div className={`p-6 rounded-2xl border shadow-sm ${pathwayCalculations.isReady ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-500/30' : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-500/30'}`} data-id="element-614">
          <div className="flex items-center gap-3 mb-4" data-id="element-615">
            <div className={`p-2 rounded-lg ${pathwayCalculations.isReady ? 'bg-emerald-100 dark:bg-emerald-800/50' : 'bg-amber-100 dark:bg-amber-800/50'}`} data-id="element-616">
              {pathwayCalculations.isReady ? <CheckCircleIcon className="w-6 h-6 text-emerald-600 dark:text-emerald-400" data-id="element-617" /> : <ClockIcon className="w-6 h-6 text-amber-600 dark:text-amber-400" data-id="element-618" />}
            </div>
            <h3 className={`text-lg font-semibold ${pathwayCalculations.isReady ? 'text-emerald-900 dark:text-emerald-100' : 'text-amber-900 dark:text-amber-100'}`} data-id="element-619">
              {pathwayCalculations.isReady ? 'You might be ready now' : 'A different approach may get you there sooner'}
            </h3>
          </div>
          {pathwayCalculations.isReady ? <p className="text-sm mb-4 text-emerald-700 dark:text-emerald-200/80" data-id="element-620">
              Based on a high-level estimate, your deposit and income suggest
              you could potentially afford your target property price. Let's
              look at the next steps.
            </p> : <div className="space-y-3" data-id="element-621">
              <p className="text-sm text-amber-700 dark:text-amber-200/80" data-id="element-622">
                It looks like your deposit may need a boost to reach your
                current target.
              </p>
              <p className="text-sm text-amber-700 dark:text-amber-200/80" data-id="element-623">
                That said, many first home buyers are surprised to learn there
                are other ways to get started — without needing years more
                saving.
              </p>
              <p className="text-sm text-amber-700 dark:text-amber-200/80" data-id="element-624">
                We can explore what pathways may be available to you.
              </p>
              <div className="mt-4 p-4 rounded-xl bg-white/60 dark:bg-white/5 border border-amber-200/50 dark:border-amber-500/20" data-id="element-625">
                <h4 className="text-sm font-semibold text-amber-900 dark:text-amber-100 mb-3" data-id="element-626">
                  Possible pathways to explore
                </h4>
                <ul className="space-y-2" data-id="element-627">
                  {['Family guarantee (buy with little to no deposit)', '5% deposit options (depending on eligibility)', 'Adjusting entry price to get into the market sooner', 'Combining strategies (deposit + borrowing optimisation)'].map((pathway, i) => <li key={i} className="flex items-start gap-2" data-id="element-628">
                      <CheckCircleIcon className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" data-id="element-629" />
                      <span className="text-sm text-amber-800 dark:text-amber-200/80" data-id="element-630">
                        {pathway}
                      </span>
                    </li>)}
                </ul>
              </div>
            </div>}
        </div>
      </div>

      <div className="flex justify-between pt-6 border-t border-slate-200 dark:border-white/10" data-id="element-631">
        <button onClick={prevStep} className="px-6 py-3 rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5 transition-colors" data-id="element-632">
          Back
        </button>
        <button onClick={nextStep} className="px-8 py-4 rounded-xl bg-gradient-to-r from-accent-500 to-primary-500 text-white font-semibold flex items-center gap-2 hover:shadow-lg hover:scale-[1.02] transition-all" data-id="element-633">
          Preview Action Plan <ArrowRightIcon className="w-5 h-5" data-id="element-634" />
        </button>
      </div>
    </motion.div>;
  const renderStep4 = () => <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} className="space-y-8" data-id="element-635">
      <div className="text-center" data-id="element-636">
        <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-3" data-id="element-637">
          Your Action Plan Preview
        </h2>
        <p className="text-slate-500 dark:text-slate-400" data-id="element-638">
          A quick look at what needs to happen next.
        </p>
      </div>

      <div className="space-y-4" data-id="element-639">
        {[{
        title: 'Structure Your Deposit',
        desc: 'Determine the optimal mix of savings, grants, and potential family guarantees.'
      }, {
        title: 'Assess Lender Options',
        desc: 'Not all banks treat first home buyers the same. We need to find the one that favours your situation.'
      }, {
        title: 'Pre-Approval Strategy',
        desc: 'Get formally assessed so you can shop for properties with confidence.'
      }].map((item, i) => <div key={i} className="flex gap-4 p-4 bg-white dark:bg-[#1a2035] rounded-xl border border-slate-200 dark:border-white/10" data-id="element-640">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 flex items-center justify-center font-bold text-sm" data-id="element-641">
              {i + 1}
            </div>
            <div data-id="element-642">
              <h4 className="font-semibold text-slate-900 dark:text-white mb-1" data-id="element-643">
                {item.title}
              </h4>
              <p className="text-sm text-slate-500 dark:text-slate-400" data-id="element-644">
                {item.desc}
              </p>
            </div>
          </div>)}
      </div>

      <div className="flex justify-between pt-6 border-t border-slate-200 dark:border-white/10" data-id="element-645">
        <button onClick={prevStep} className="px-6 py-3 rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5 transition-colors" data-id="element-646">
          Back
        </button>
        <button onClick={nextStep} className="px-8 py-4 rounded-xl bg-gradient-to-r from-accent-500 to-primary-500 text-white font-semibold flex items-center gap-2 hover:shadow-lg hover:scale-[1.02] transition-all" data-id="element-647">
          Get Full Result <ArrowRightIcon className="w-5 h-5" data-id="element-648" />
        </button>
      </div>
    </motion.div>;
  const renderStep5 = () => <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} className="space-y-8 max-w-md mx-auto" data-id="element-649">
      <div className="text-center" data-id="element-650">
        <div className="w-16 h-16 bg-primary-50 dark:bg-primary-500/10 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-651">
          <ShieldCheckIcon className="w-8 h-8 text-primary-500" data-id="element-652" />
        </div>
        <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-3" data-id="element-653">
          Unlock Your Full Result
        </h2>
        <p className="text-slate-500 dark:text-slate-400" data-id="element-654">
          Enter your details to see your comprehensive summary and download your
          free guide.
        </p>
      </div>

      <div className="space-y-4 bg-white dark:bg-[#1a2035] p-6 rounded-2xl border border-slate-200 dark:border-white/10 shadow-sm" data-id="element-655">
        <div data-id="element-656">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1" data-id="element-657">
            Full Name
          </label>
          <input type="text" value={formData.name} onChange={e => handleInputChange('name', e.target.value)} className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-[#0f1629] text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 outline-none transition-all" placeholder="John Doe" data-id="element-658" />
        </div>
        <div data-id="element-659">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1" data-id="element-660">
            Email Address
          </label>
          <input type="email" value={formData.email} onChange={e => handleInputChange('email', e.target.value)} className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-[#0f1629] text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 outline-none transition-all" placeholder="john@example.com" data-id="element-661" />
        </div>
        <div data-id="element-662">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1" data-id="element-663">
            Phone Number
          </label>
          <input type="tel" value={formData.phone} onChange={e => handleInputChange('phone', e.target.value)} className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-[#0f1629] text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 outline-none transition-all" placeholder="0400 000 000" data-id="element-664" />
        </div>
        <button onClick={nextStep} disabled={!formData.name || !formData.email} className="w-full mt-4 px-8 py-4 rounded-xl bg-gradient-to-r from-accent-500 to-primary-500 text-white font-semibold flex items-center justify-center gap-2 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all" data-id="element-665">
          Reveal My Strategy <ChevronRightIcon className="w-5 h-5" data-id="element-666" />
        </button>
        <p className="text-xs text-center text-slate-400 mt-4" data-id="element-667">
          We respect your privacy. No spam, ever.
        </p>
      </div>
    </motion.div>;
  const renderStep6 = () => <motion.div initial={{
    opacity: 0,
    scale: 0.95
  }} animate={{
    opacity: 1,
    scale: 1
  }} exit={{
    opacity: 0,
    scale: 0.95
  }} className="space-y-8" data-id="element-668">
      <div className="text-center" data-id="element-669">
        <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-3" data-id="element-670">
          Hi {formData.name || 'there'}, here is your summary
        </h2>
        <p className="text-slate-500 dark:text-slate-400" data-id="element-671">
          A snapshot of your current position and potential.
        </p>
      </div>

      <div className="bg-white dark:bg-[#1a2035] rounded-2xl border border-slate-200 dark:border-white/10 shadow-sm overflow-hidden" data-id="element-672">
        <div className="bg-slate-50 dark:bg-white/5 p-6 border-b border-slate-200 dark:border-white/10" data-id="element-673">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4" data-id="element-674">
            Your Profile
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-id="element-675">
            <div data-id="element-676">
              <p className="text-xs text-slate-500 mb-1" data-id="element-677">Target Price</p>
              <p className="font-medium text-slate-900 dark:text-white" data-id="element-678">
                {formatCurrency(formData.purchasePrice)}
              </p>
            </div>
            <div data-id="element-679">
              <p className="text-xs text-slate-500 mb-1" data-id="element-680">Current Savings</p>
              <p className="font-medium text-slate-900 dark:text-white" data-id="element-681">
                {formatCurrency(formData.savings)}
              </p>
            </div>
            <div data-id="element-682">
              <p className="text-xs text-slate-500 mb-1" data-id="element-683">Total Income</p>
              <p className="font-medium text-slate-900 dark:text-white" data-id="element-684">
                {formatCurrency(formData.annualIncome + formData.partnerIncome + formData.secondaryIncome)}
              </p>
            </div>
            <div data-id="element-685">
              <p className="text-xs text-slate-500 mb-1" data-id="element-686">State</p>
              <p className="font-medium text-slate-900 dark:text-white" data-id="element-687">
                {formData.state}
              </p>
            </div>
          </div>
        </div>
        <div className="p-6" data-id="element-688">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4" data-id="element-689">
            Key Insights
          </h3>
          <ul className="space-y-3" data-id="element-690">
            <li className="flex items-start gap-3" data-id="element-691">
              <CheckCircleIcon className="w-5 h-5 text-primary-500 flex-shrink-0 mt-0.5" data-id="element-692" />
              <span className="text-sm text-slate-600 dark:text-slate-300" data-id="element-693">
                Your current deposit is{' '}
                <strong data-id="element-694">
                  {pathwayCalculations.depositPercent.toFixed(1)}%
                </strong>{' '}
                of your target price.
              </span>
            </li>
            <li className="flex items-start gap-3" data-id="element-695">
              <CheckCircleIcon className="w-5 h-5 text-primary-500 flex-shrink-0 mt-0.5" data-id="element-696" />
              <span className="text-sm text-slate-600 dark:text-slate-300" data-id="element-697">
                Your estimated borrowing capacity is around{' '}
                <strong data-id="element-698">
                  {formatCurrency(pathwayCalculations.borrowingEstimate)}
                </strong>
                .
              </span>
            </li>
            <li className="flex items-start gap-3" data-id="element-699">
              <CheckCircleIcon className="w-5 h-5 text-primary-500 flex-shrink-0 mt-0.5" data-id="element-700" />
              <span className="text-sm text-slate-600 dark:text-slate-300" data-id="element-701">
                Waiting 12 months could cost you an estimated{' '}
                <strong data-id="element-702">
                  {formatCurrency(formData.weeklyRent * 4.33 * 12 + formData.purchasePrice * 0.05)}
                </strong>{' '}
                in rent and missed growth.
              </span>
            </li>
          </ul>
        </div>
      </div>

      <div className="flex justify-center pt-4" data-id="element-703">
        <button onClick={nextStep} className="px-8 py-4 rounded-xl bg-gradient-to-r from-accent-500 to-primary-500 text-white font-semibold flex items-center gap-2 hover:shadow-lg hover:scale-[1.02] transition-all" data-id="element-704">
          Continue to Downloads <ArrowRightIcon className="w-5 h-5" data-id="element-705" />
        </button>
      </div>
    </motion.div>;
  const renderStep7 = () => <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} className="space-y-8 text-center" data-id="element-706">
      <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-3" data-id="element-707">
        Your Free Guide
      </h2>
      <p className="text-slate-500 dark:text-slate-400 mb-8" data-id="element-708">
        Download our comprehensive guide for first home buyers.
      </p>

      <div className="max-w-sm mx-auto bg-white dark:bg-[#1a2035] p-8 rounded-2xl border border-slate-200 dark:border-white/10 shadow-lg relative overflow-hidden group" data-id="element-709">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-500/5 to-accent-500/5 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-710"></div>
        <div className="w-20 h-24 bg-slate-100 dark:bg-slate-800 rounded-lg mx-auto mb-6 flex items-center justify-center shadow-inner relative" data-id="element-711">
          <div className="absolute -right-2 -top-2 w-8 h-8 bg-accent-500 rounded-full flex items-center justify-center text-white shadow-md" data-id="element-712">
            <DownloadIcon className="w-4 h-4" data-id="element-713" />
          </div>
          <span className="text-xs font-bold text-slate-400" data-id="element-714">PDF</span>
        </div>
        <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2" data-id="element-715">
          The FHB Playbook
        </h3>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-6" data-id="element-716">
          Everything you need to know about grants, schemes, and structuring
          your first loan.
        </p>
        <button className="w-full py-3 rounded-xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-semibold flex items-center justify-center gap-2 hover:bg-slate-800 dark:hover:bg-slate-100 transition-colors" data-id="element-717">
          <DownloadIcon className="w-4 h-4" data-id="element-718" /> Download Now
        </button>
      </div>

      <div className="pt-8" data-id="element-719">
        <button onClick={nextStep} className="text-primary-600 dark:text-primary-400 font-medium hover:underline flex items-center justify-center gap-1 mx-auto" data-id="element-720">
          Next: Book a Strategy Session <ArrowRightIcon className="w-4 h-4" data-id="element-721" />
        </button>
      </div>
    </motion.div>;
  const renderStep8 = () => <motion.div initial={{
    opacity: 0,
    scale: 0.95
  }} animate={{
    opacity: 1,
    scale: 1
  }} className="text-center space-y-8 py-12" data-id="element-722">
      <div className="w-20 h-20 bg-gradient-to-br from-accent-500 to-primary-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-primary-500/30" data-id="element-723">
        <CheckCircleIcon className="w-10 h-10 text-white" data-id="element-724" />
      </div>
      <h2 className="text-4xl font-bold text-slate-900 dark:text-white mb-4" data-id="element-725">
        You're all set!
      </h2>
      <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto mb-8" data-id="element-726">
        You have your numbers, you have your guide. The next step is to sit down
        and map out the exact strategy to get you into your property.
      </p>

      <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-727">
        <a href="/#contact" className="px-8 py-4 rounded-xl bg-gradient-to-r from-accent-500 to-primary-500 text-white font-semibold text-lg hover:shadow-lg hover:scale-[1.02] transition-all" data-id="element-728">
          Book Strategy Session
        </a>
        <button onClick={() => setCurrentStep(1)} className="px-8 py-4 rounded-xl bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-700 dark:text-slate-300 font-semibold hover:bg-slate-50 dark:hover:bg-white/10 transition-all" data-id="element-729">
          Start Over
        </button>
      </div>
    </motion.div>;
  return <div className="max-w-4xl mx-auto" data-id="element-730">
      {currentStep < 8 && <ProgressBar data-id="element-731" />}

      <div className="bg-white dark:bg-[#0f1629] rounded-3xl shadow-xl border border-slate-200 dark:border-white/10 p-6 md:p-10 min-h-[600px] relative overflow-hidden" data-id="element-732">
        {/* Decorative background elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0" data-id="element-733">
          <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-primary-500/5 blur-[80px]" data-id="element-734"></div>
          <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-accent-500/5 blur-[80px]" data-id="element-735"></div>
        </div>

        <div className="relative z-10" data-id="element-736">
          <AnimatePresence mode="wait" data-id="element-737">
            {currentStep === 1 && <div key="step1" data-id="element-738">{renderStep1()}</div>}
            {currentStep === 2 && <div key="step2" data-id="element-739">{renderStep2()}</div>}
            {currentStep === 3 && <div key="step3" data-id="element-740">{renderStep3()}</div>}
            {currentStep === 4 && <div key="step4" data-id="element-741">{renderStep4()}</div>}
            {currentStep === 5 && <div key="step5" data-id="element-742">{renderStep5()}</div>}
            {currentStep === 6 && <div key="step6" data-id="element-743">{renderStep6()}</div>}
            {currentStep === 7 && <div key="step7" data-id="element-744">{renderStep7()}</div>}
            {currentStep === 8 && <div key="step8" data-id="element-745">{renderStep8()}</div>}
          </AnimatePresence>
        </div>
      </div>
    </div>;
}